import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { EquipmentItemDto, ProtocolDetailDto, ProtocolFilterDto, ProtocolService, ProtocolTypeService, UserDetailDto } from '@artemis-software/wr-api';
import { CurrentUserService } from '@services/current-user.service';
import { currentTenantId } from '@/utils/admin-utils';
import { NbDialogService } from '@nebular/theme';
import {
  ProtocolCreateDialogComponent,
  ProtocolCreateResult,
} from '@components/dialog/protocol-create-dialog/protocol-create-dialog.component';

@Component({
  standalone: false,
  selector: 'wr-building-protocol-overview',
  templateUrl: './building-protocol-overview.component.html',
  styleUrl: './building-protocol-overview.component.scss',
})
export class BuildingProtocolOverviewComponent {

  private readonly router = inject(Router);
  private readonly protocolService = inject(ProtocolService);
  private readonly protocolTypeService = inject(ProtocolTypeService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly fb = inject(FormBuilder);
  private readonly dialogService = inject(NbDialogService);

  readonly buildingId = input.required<string | null>();
  readonly tenantId = currentTenantId();
  readonly canCreate = computed(() => this.currentUserService.hasPrivilege('canCreateProtocols'));

  readonly form: FormGroup;
  readonly protocolTypes$ = this.protocolTypeService.findAll({ tenantId: this.tenantId() });

  readonly loadItems = (filter: ProtocolFilterDto & { equipmentId?: EquipmentItemDto | string }) =>
    this.protocolService.findAllDetailDtos({
      ...filter,
      tenantId: this.tenantId(),
      buildingId: this.buildingId() ?? undefined,
      includeBuildingEquipmentProtocols: true,
      includeAllBuildingProtocols: true,
      equipmentId: this.extractEquipmentId(filter.equipmentId),
    } as any);

  readonly getCount = (filter: ProtocolFilterDto & { equipmentId?: EquipmentItemDto | string }) =>
    this.protocolService.getCount({
      ...filter,
      tenantId: this.tenantId(),
      buildingId: this.buildingId() ?? undefined,
      includeBuildingEquipmentProtocols: true,
      includeAllBuildingProtocols: true,
      equipmentId: this.extractEquipmentId(filter.equipmentId),
    } as any);

  constructor() {
    this.form = this.fb.group({
      text: [''],
      equipmentId: [null],
      protocolTypeId: [null],
    });
  }

  private extractEquipmentId(value: EquipmentItemDto | string | undefined | null): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return value.id;
  }

  navigateToDetail(item: ProtocolDetailDto): void {
    this.router.navigate(['protocols', item.id]);
  }

  getUserProfile(user: UserDetailDto | undefined): string {
    const fallback = 'https://i.imgur.com/ujW6tKI.png';
    return user?.userProfilePicture?.presignedUrl ?? fallback;
  }

  getCreatorName(item: ProtocolDetailDto): string {
    if (item.creator) {
      return `${item.creator.firstName} ${item.creator.lastName}`;
    }
    return item.creatorInfo?.name || 'Anonym';
  }

  readonly exportCreatorName = (item: ProtocolDetailDto): string => this.getCreatorName(item);

  createProtocol(): void {
    const buildingId = this.buildingId();
    this.dialogService.open(ProtocolCreateDialogComponent, {
      context: { preselectedBuildingId: buildingId },
    }).onClose.subscribe((result: ProtocolCreateResult | null) => {
      if (!result) return;
      const queryParams: Record<string, string> = { protocolTypeId: result.protocolTypeId };
      if (result.buildingId) queryParams['buildingId'] = result.buildingId;
      if (result.buildingEquipmentId) queryParams['buildingEquipmentId'] = result.buildingEquipmentId;
      this.router.navigate(['protocols', 'new'], { queryParams });
    });
  }
}
